import { RegisterComponent } from '@/common/register-component';
import { NylasSchedulerConfigConnector } from '@/connector/nylas-scheduler-config-connector';
import { NylasSchedulerEditor } from '../nylas-scheduler-editor/nylas-scheduler-editor';
import { AttachInternals, Component, Element, Event, EventEmitter, h, Host, Listen, Prop, State, Watch } from '@stencil/core';
import { Calendar, CalendarOptions } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { debug } from '@/utils/utils';
import i18next from '@/utils/i18n';
import { LANGUAGE_CODE } from '@/common/constants';
import { Conference, EventDetails, GroupConfiguration, GroupEventAPIData, NylasCalendar, RecurrenceDeleteOption, RecurrenceUpdateOption } from '@nylas/core';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { User } from '@/common/nylas-api-request';

dayjs.extend(utc);
dayjs.extend(timezone);

type CalendarEventData = {
  title: string;
  start: string;
  end: string;
  calendar_id: string;
  id: string;
  classNames: string[];
};

export type CreateGroupEventFormState = {
  title: string;
  description: string;
  location: string;
  eventTimeslot: {
    when: {
      start_time: number;
      end_time: number;
      start_timezone: string;
      end_timezone: string;
    };
    recurrence: string[];
  };
  eventCapacity: number;
  conferencing?: Conference;
  participants: {
    name: string;
    email: string;
    is_organizer?: boolean;
  }[];
};

const typeToViewMapping = {
  day: 'timeGridDay',
  week: 'timeGridWeek',
  month: 'dayGridMonth',
};

const viewToTypeMapping = {
  timeGridDay: 'day',
  timeGridWeek: 'week',
  dayGridMonth: 'month',
};

const editChoices = [
  { label: i18next.t('nylasEventCalendar.editOptions.import') || 'Import', value: 'import', langKey: 'nylasEventCalendar.editOptions.import' },
  { label: i18next.t('nylasEventCalendar.editOptions.edit') || 'Edit', value: 'edit', langKey: 'nylasEventCalendar.editOptions.edit' },
  { label: i18next.t('nylasEventCalendar.editOptions.deleteThis') || 'Delete this event only', value: 'delete-this', langKey: 'nylasEventCalendar.editOptions.deleteThis' },
  // { label: 'Delete this and all future occurrences', value: 'delete-future' },
  { label: i18next.t('nylasEventCalendar.editOptions.deleteAll') || 'Delete all events', value: 'delete-all', langKey: 'nylasEventCalendar.editOptions.deleteAll' },
];

const updateChoices: {
  label: string;
  value: RecurrenceUpdateOption;
  langKey: string;
}[] = [
  { label: i18next.t('nylasEventCalendar.updateOptions.updateThis') || 'Update this event', value: 'this', langKey: 'nylasEventCalendar.updateOptions.updateThis' },
  {
    label: i18next.t('nylasEventCalendar.updateOptions.updateFuture') || 'Update all future occurrences',
    value: 'future',
    langKey: 'nylasEventCalendar.updateOptions.updateFuture',
  },
  { label: i18next.t('nylasEventCalendar.updateOptions.updateAll') || 'Update all events', value: 'all', langKey: 'nylasEventCalendar.updateOptions.updateAll' },
];

const deleteChoices: {
  label: string;
  value: RecurrenceDeleteOption;
  langKey: string;
}[] = [
  { label: i18next.t('nylasEventCalendar.deleteOptions.deleteThis') || 'Delete this event', value: 'this', langKey: 'nylasEventCalendar.deleteOptions.deleteThis' },
  {
    label: i18next.t('nylasEventCalendar.deleteOptions.deleteFuture') || 'Delete this and all future occurrences',
    value: 'future',
    langKey: 'nylasEventCalendar.deleteOptions.deleteFuture',
  },
  { label: i18next.t('nylasEventCalendar.deleteOptions.deleteAll') || 'Delete all events', value: 'all', langKey: 'nylasEventCalendar.deleteOptions.deleteAll' },
];

@Component({
  tag: 'nylas-event-calendar',
  styleUrl: 'nylas-event-calendar.scss',
  shadow: true,
  formAssociated: true,
})
export class NylasEventCalendar {
  @Element() host!: HTMLNylasEventCalendarElement;
  private calendarEl!: HTMLElement;
  private formRef!: HTMLFormElement;
  /**
   * @standalone
   * The selected configuration
   */
  @Prop() selectedConfiguration?: GroupConfiguration;
  /**
   * @standalone
   * The name of the component
   */
  @Prop() name: string = 'page-name';
  /**
   * The selected language.
   */
  @Prop() selectedLanguage?: LANGUAGE_CODE;
  /**
   * The group events prop
   */
  @Prop() groupEvents?: GroupEventAPIData[];

  /**
   * The current user prop
   */
  @Prop() currentUser?: User;
  /**
   * The calendars prop
   */
  @Prop() calendars?: NylasCalendar[];
  /**
   * The master group events prop
   */
  @Prop() masterGroupEvents?: Record<string, EventDetails>;
  /**
   * The is loading prop
   */
  @Prop() isLoading?: boolean;
  /**
   * The element internals.
   */
  @AttachInternals() internals!: ElementInternals;
  @State() selectedView: 'day' | 'week' | 'month' = 'week';
  @State() events: {
    title: string;
    start: string;
    end: string;
    calendar_id: string;
    id: string;
    classNames: string[];
  }[] = [];

  @State() calendar!: Calendar;

  @State() selectedEvent: GroupEventAPIData | null = null;

  @State() selectedDateRangeString: string = '';

  @State() showEditEventDetails: boolean = false;

  @State() showCreateEventDetails: boolean = false;

  @State() editOptionChoices: { label: string; value: string; langKey: string }[] = editChoices;

  @State() updateOptionChoices: { label: string; value: 'this' | 'future' | 'all'; langKey: string }[] = updateChoices;

  @State() deleteOptionChoices: { label: string; value: string; langKey: string }[] = deleteChoices;

  @State() editOptions: { label: string; value: string }[] = this.editOptionChoices;

  @State() showEditOptions: boolean = false;

  @State() updateOptions: { label: string; value: 'this' | 'future' | 'all' }[] = this.updateOptionChoices;

  @State() showUpdateOptions: boolean = false;

  @State() deleteOptions: { label: string; value: string }[] = this.deleteOptionChoices;

  @State() showDeleteOptions: boolean = false;

  @State() unsavedChanges: boolean = false;

  @State() showConfirmDiscardUnsavedChangesModal: boolean = false;

  @State() createNewGroupEventTimeslot: {
    when: {
      start_time: number;
      end_time: number;
      start_timezone: string;
      end_timezone: string;
    };
    recurrence: string[];
  } | null = null;
  @State() formError: string = '';
  @State() formErrors: Record<string, string> = {};

  @State() loadingOptions: {
    update: string;
    delete: string;
    create: string;
  } = {
    update: '',
    delete: '',
    create: '',
  };

  @State() showChooseParticipantsModal: boolean = false;

  @State() importParticipants: { label: string; value: string; disabled?: boolean }[] = [];

  @State() selectedImportParticipants: { email: string; status: string }[] = [];

  @State() showAllEvents: boolean = true;

  @Event() fetchMasterGroupEvent!: EventEmitter<string>;

  @Event() saveGroupEventChanges!: EventEmitter<{ data: GroupEventAPIData; action: 'this' | 'future' | 'all' }>;

  @Event() deleteGroupEvent!: EventEmitter<{ data: GroupEventAPIData; action: 'this' | 'future' | 'all' }>;

  @Event() groupEventSelected!: EventEmitter<GroupEventAPIData>;

  @Event() createGroupEvent!: EventEmitter<CreateGroupEventFormState>;

  @Event() calendarViewChanged!: EventEmitter<{ startTime: number; endTime: number }>;

  @Event() previosNextClicked!: EventEmitter<{ direction: 'prev' | 'next'; view: 'day' | 'week' | 'month'; currentViewRange: { startTime: number; endTime: number } }>;

  @Event() setCalendarViewDateRange!: EventEmitter<{ startTime: number; endTime: number }>;

  @Event() importGroupEvent!: EventEmitter<{
    event_id: string;
    calendar_id: string;
  }>;
  /**
   * When a name prop is passed, stencil does not automatically set the name attribute on the host element.
   * Since this component is form-associated, the name attribute is required for form submission.
   * This is a workaround to ensure that the name attribute is set on the host element.
   */
  @Watch('name')
  elementNameChangedHandler(newValue: string) {
    debug('nylas-page-name', 'elementNameChangedHandler', newValue);
    this.host.setAttribute('name', newValue);
  }

  /**
   * Create a custom locale object for FullCalendar with translations
   */
  createCustomLocaleConfig(lang: LANGUAGE_CODE) {
    return {
      code: lang,
      buttonText: {
        today: i18next.t('today') || 'today',
        month: i18next.t('timePeriod_month') || 'month',
        week: i18next.t('timePeriod_week') || 'week',
        day: i18next.t('timePeriod_day') || 'day',
      },
      allDayText: i18next.t('allDay') || 'all-day',
    };
  }

  @Watch('selectedLanguage')
  selectedLanguageChangedHandler(newVal: LANGUAGE_CODE, oldVal: LANGUAGE_CODE) {
    if (newVal === oldVal) return;
    i18next.changeLanguage(newVal);
    if (this.calendar) {
      const localeConfig = this.createCustomLocaleConfig(newVal);
      this.calendar.setOption('locale', localeConfig);
    }

    // Update the options with new translations
    this.updateOptionTranslations();
  }

  /**
   * Updates all option labels with current translations
   */
  private updateOptionTranslations() {
    this.editOptionChoices = this.editOptionChoices.map(option => ({
      ...option,
      label: i18next.t(option.langKey) || option.label,
    }));
    this.updateOptionChoices = this.updateOptionChoices.map(option => ({
      ...option,
      label: i18next.t(option.langKey) || option.label,
    }));
    this.deleteOptionChoices = this.deleteOptionChoices.map(option => ({
      ...option,
      label: i18next.t(option.langKey) || option.label,
    }));
    this.editOptions = this.editOptions.map(option => ({
      ...option,
      label: this.editOptionChoices.find(o => o.value === option.value)?.label || option.label,
    }));
    this.updateOptions = this.updateOptions.map(option => ({
      ...option,
      label: this.updateOptionChoices.find(o => o.value === option.value)?.label || option.label,
    }));
    this.deleteOptions = this.deleteOptions.map(option => ({
      ...option,
      label: this.deleteOptionChoices.find(o => o.value === option.value)?.label || option.label,
    }));
  }

  @Watch('groupEvents')
  groupEventsChangedHandler(newValue: GroupEventAPIData[], oldValue: GroupEventAPIData[]) {
    if (newValue === oldValue) return;
    this.events = newValue
      .filter(event => event !== undefined)
      .map(eventData => {
        const event = eventData.event;
        if (event?.id) {
          const isGroupEvent = !!eventData.group_event_info && !!eventData.group_event_info?.event_id;
          const readOnly = event.read_only;
          const eventParticipants = event.participants || [];
          const groupEventParticipants = eventData.group_event_info?.participants || [];
          const noParticipants = eventParticipants.length <= 0 && groupEventParticipants.length <= 0;
          return {
            title: event.title,
            start: dayjs
              .unix(event.when.start_time) // Convert UNIX timestamp
              .locale(this.selectedLanguage || 'en')
              .format('YYYY-MM-DDTHH:mm:ss'),
            end: dayjs
              .unix(event.when.end_time) // Convert UNIX timestamp
              .locale(this.selectedLanguage || 'en')
              .format('YYYY-MM-DDTHH:mm:ss'),
            calendar_id: event.calendar_id,
            id: event.id,
            classNames: [isGroupEvent ? 'group-event' : 'regular-event', readOnly ? 'read-only' : '', noParticipants ? 'no-participants' : ''],
            editable: false,
          };
        }
        return undefined;
      }) as CalendarEventData[];
    let events = JSON.parse(JSON.stringify(this.events));
    if (!this.showAllEvents) {
      events = events.filter(event => event?.classNames.includes('group-event'));
    }
    this.calendar?.setOption('events', events);
  }

  // Lifecycle Methods
  connectedCallback() {
    debug('nylas-connected-calendars', 'connectedCallback');
  }

  disconnectedCallback() {
    debug('nylas-connected-calendars', 'disconnectedCallback');
  }

  componentWillLoad() {
    debug('nylas-connected-calendars', 'componentWillLoad');
  }

  componentDidLoad() {
    this.selectedView = 'week';
    const calendarOptions: CalendarOptions = {
      plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
      headerToolbar: false,
      initialView: 'timeGridWeek',
      events: this.events,
      editable: true, // Allow drag-and-drop
      selectable: true,
      nowIndicator: true,
      scrollTime: '05:00:00', // Scroll to 6 AM (formatted as HH:MM:SS)
      dayHeaderFormat: { weekday: 'short', month: undefined, day: '2-digit', formatMatcher: 'basic' }, // Customize header format
      dayHeaderContent: headerInfo => {
        if (headerInfo.view.type === 'dayGridMonth') {
          return {
            html: `<div class="custom-day-header">
              <span class="day-name">${dayjs(headerInfo.date)
                .locale(this.selectedLanguage || 'en')
                .format('ddd')}</span>
            </div>`,
          };
        }
        return {
          html: `<div class="custom-day-header">
              <span class="day-name">${dayjs(headerInfo.date)
                .locale(this.selectedLanguage || 'en')
                .format('ddd')}</span>
              <span class="day-number ${headerInfo.isToday ? 'today' : ''}">${dayjs(headerInfo.date)
                .locale(this.selectedLanguage || 'en')
                .format('D')}</span>
            </div>`,
        };
      },
      datesSet: info => {
        const startMonth = dayjs(info.start)
          .locale(this.selectedLanguage || 'en')
          .format('MMMM')
          .substring(0, 3); // Get the first 3 characters of the month
        const endMonth = dayjs(info.end)
          .subtract(1, 'day')
          .locale(this.selectedLanguage || 'en')
          .format('MMMM')
          .substring(0, 3); // Subtract 1 day for the correct end month
        const startYear = dayjs(info.start).format('YYYY');
        const endYear = dayjs(info.end).subtract(1, 'day').format('YYYY');

        if (startMonth === endMonth && startYear === endYear) {
          // Same month and year
          this.selectedDateRangeString = `${startMonth} ${startYear}`;
        } else if (startYear === endYear) {
          // Different months but same year
          this.selectedDateRangeString = `${startMonth} - ${endMonth} ${startYear}`;
        } else {
          // Different months and different years
          this.selectedDateRangeString = `${startMonth} ${startYear} - ${endMonth} ${endYear}`;
        }

        // When view changes, reset the observer to ensure scrolling works
        if (info.view.type.includes('timeGrid')) {
          // Use MutationObserver to detect when scrollers are ready
          this.observeCalendarRendering();
        }
      },
      eventClick: info => {
        const event = this.groupEvents?.find(e => e.event.id === info.event._def.publicId);

        if (info.event.classNames.includes('read-only') || info.event.classNames.includes('no-participants')) {
          return;
        }

        this.showEditEventDetails = false;
        this.unsavedChanges = false;
        this.showEditOptions = true;
        this.selectedEvent = event || null;

        if (event && event.group_event_info) {
          const masterEventId = event.event.master_event_id;
          this.groupEventSelected.emit(event);
          this.editOptions = this.editOptionChoices.filter(option => option.value !== 'import');
          this.deleteOptions = [...this.deleteOptionChoices];
          this.updateOptions = [...this.updateOptionChoices];
          if (!masterEventId) {
            this.editOptions = this.editOptions.filter(option => option.value !== 'delete-all');
            this.deleteOptions = this.deleteOptions.filter(option => option.value !== 'all' && option.value !== 'future');
          }
          if (masterEventId && !this.masterGroupEvents?.[masterEventId]) {
            this.fetchMasterGroupEvent.emit(event.event.master_event_id);
          }
        } else {
          this.editOptions = this.editOptionChoices.filter(option => option.value === 'import');
        }
      },
      select: (info: any) => {
        if (new Date(info.start).getTime() < new Date().getTime()) {
          return;
        }
        this.createNewGroupEventTimeslot = {
          when: {
            start_time: new Date(info.start).getTime() / 1000,
            end_time: new Date(info.end).getTime() / 1000,
            start_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            end_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
          recurrence: [],
        };
        this.showCreateEventDetails = true;
      },
      eventDurationEditable: true,
      height: '100%', // Let FullCalendar adjust dynamically
      contentHeight: 'auto', // Adjust content height based on events
    };

    this.calendar = new Calendar(this.calendarEl, calendarOptions);
    this.calendar.render();
    if (this.selectedLanguage) {
      const localeConfig = this.createCustomLocaleConfig(this.selectedLanguage);
      this.calendar.setOption('locale', localeConfig);
      // Initialize with correct translations
      this.updateOptionTranslations();
    }

    // Set up a MutationObserver to detect when FullCalendar has fully rendered its DOM
    this.observeCalendarRendering();

    this.setCalendarViewDateRange.emit({
      startTime: new Date(this.calendar.getCurrentData().viewApi.activeStart).getTime() / 1000,
      endTime: new Date(this.calendar.getCurrentData().viewApi.activeEnd).getTime() / 1000,
    });
  }

  /**
   * Set up MutationObserver to detect when the calendar is fully rendered
   */
  private observeCalendarRendering() {
    // Create a MutationObserver to watch for changes in the DOM
    const observer = new MutationObserver(() => {
      // Check if the scroller elements are present
      const timeGridScrollers = this.calendarEl.querySelectorAll('.fc-timegrid-body .fc-scroller');

      if (timeGridScrollers.length > 0) {
        // Found the scrollers, disconnect observer and scroll
        observer.disconnect();

        // Apply scroll to time grid scrollers
        timeGridScrollers.forEach(scroller => {
          const scrollElement = scroller as HTMLElement;
          scrollElement.style.overflowY = 'auto';
          // Scroll to 9 AM (approximately 9 * 48px per hour in most implementations)
          const scrollPosition = 8 * 48;
          scrollElement.scrollTop = scrollPosition;

          // Force layout recalculation to ensure scroll is applied
          void scrollElement.offsetHeight;
        });

        // Also inject CSS to help with scrolling
        this.injectScrollStyles();
      }
    });

    // Start observing with configuration
    observer.observe(this.calendarEl, {
      childList: true,
      subtree: true,
    });

    // Set a timeout to disconnect the observer and try additional scrolling methods
    setTimeout(() => {
      observer.disconnect();
      this.forceBrowserScrollToTime();
    }, 20);
  }

  /**
   * Inject CSS to help with scroll positioning as a fallback
   */
  private injectScrollStyles() {
    // Create a unique ID for this calendar instance to avoid conflicts
    const styleId = `nylas-calendar-scroll-${Math.random().toString(36).substring(2, 9)}`;

    // Remove any existing style with this ID
    const existingStyle = this.host.shadowRoot?.querySelector(`#${styleId}`);
    if (existingStyle) {
      existingStyle.remove();
    }

    // Create and inject the style element with more specific selectors
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      .fc-timegrid-body .fc-scroller,
      .fc-scroller-liquid-absolute,
      .fc-scroller {
        overflow-y: auto;
        overflow-x: hidden;
        scroll-padding-top: 0;
        scroll-behavior: auto;
      }
      
      .fc-timegrid-body {
        overflow: visible;
      }
      
      .fc-timegrid-slots tr:nth-child(8) {
        scroll-margin-top: 0;
      }
    `;

    this.host.shadowRoot?.appendChild(style);
  }

  /**
   * Last resort method to force browser to scroll to the correct time
   * by directly manipulating the DOM and using scrollIntoView
   */
  private forceBrowserScrollToTime() {
    try {
      // Try to find time slot elements directly
      const hourSlots = this.calendarEl.querySelectorAll('.fc-timegrid-slot[data-time]');
      let fiveAmSlot: Element | null = null;

      // Look for the 5 AM slot
      hourSlots.forEach(slot => {
        const dataTime = slot.getAttribute('data-time');
        if (dataTime && dataTime.startsWith('05:')) {
          fiveAmSlot = slot;
        }
      });

      // If found, scroll to it
      if (fiveAmSlot) {
        (fiveAmSlot as HTMLElement).scrollIntoView({ behavior: 'auto', block: 'start' });
      } else {
        // Alternative approach: find time labels
        const timeLabels = this.calendarEl.querySelectorAll('.fc-timegrid-axis-cushion');
        timeLabels.forEach(label => {
          if (label.textContent?.includes('5') || label.textContent?.includes('05')) {
            (label as HTMLElement).scrollIntoView({ behavior: 'auto', block: 'start' });
          }
        });
      }
    } catch (error) {
      console.error('Error in direct time scroll:', error);
    }
  }

  setView(view: 'day' | 'week' | 'month') {
    this.selectedView = view;
    this.calendar.changeView(typeToViewMapping[view]);
    const startTime = new Date(this.calendar.getCurrentData().viewApi.activeStart).getTime() / 1000;
    const endTime = new Date(this.calendar.getCurrentData().viewApi.activeEnd).getTime() / 1000;
    this.calendarViewChanged.emit({ startTime, endTime });

    // Add scroll after view change if it's a time grid view (day or week)
    if (view === 'day' || view === 'week') {
      // Use MutationObserver to detect when scrollers are ready after view change
      this.observeCalendarRendering();
    }
  }

  dayHeaderContent(headerInfo: any) {
    if (headerInfo.view.type === 'dayGridMonth') {
      return {
        html: `<div class="custom-day-header">
            <span class="day-name">${dayjs(headerInfo.date)
              .locale(this.selectedLanguage || 'en')
              .format('ddd')}</span>
          </div>`,
      };
    }
    return {
      html: `<div class="custom-day-header">
          <span class="day-name">${dayjs(headerInfo.date)
            .locale(this.selectedLanguage || 'en')
            .format('ddd')}</span>
          <span class="day-number ${headerInfo.isToday ? 'today' : ''}">${dayjs(headerInfo.date)
            .locale(this.selectedLanguage || 'en')
            .format('D')}</span>
        </div>`,
    };
  }

  handleEditOptionSelected(e: Event, option: { label: string; value: string }) {
    e.preventDefault();
    e.stopPropagation();

    if (this.isLoading) return;
    if (option.value === 'edit') {
      this.showEditOptions = false;
      this.showEditEventDetails = true;
    } else if ((option.value === 'delete-this' || option.value === 'delete-all' || option.value === 'delete-future') && this.selectedEvent) {
      this.loadingOptions.delete = option.value;
      const action = option.value.split('-')[1] as RecurrenceDeleteOption;
      this.deleteGroupEvent.emit({ data: this.selectedEvent, action });
    } else if (option.value === 'import' && this.selectedEvent) {
      const participants = this.selectedEvent.event.participants || [];
      const organizer = this.selectedEvent.event.organizer;

      this.showEditOptions = false;
      this.showChooseParticipantsModal = true;
      this.importParticipants = participants.map(participant => ({
        label: participant?.email,
        value: participant?.email,
        disabled: participant?.email === organizer?.email,
        selected: participant?.email === organizer?.email,
      }));
    }
  }

  handleImportGroupEvent() {
    const masterEventId = this.selectedEvent?.event.master_event_id;
    const participantsList = this.selectedImportParticipants;
    const organizer = this.selectedEvent?.event.organizer;
    const groupEvent = {
      event_id: masterEventId ? masterEventId : this.selectedEvent?.event.id || '',
      calendar_id: this.selectedEvent?.event.calendar_id || '',
      capacity: 10,
      participants: participantsList.length > 0 ? participantsList : [{ ...organizer, is_organizer: true }],
      organizer: organizer,
    };
    this.showChooseParticipantsModal = false;
    if (!!groupEvent.event_id && !!groupEvent.calendar_id) {
      this.importGroupEvent.emit(groupEvent);
    }
  }

  handlePrevNextClick(direction: 'prev' | 'next') {
    const currentView = viewToTypeMapping[this.calendar.view.type];
    if (direction === 'prev') {
      this.calendar.prev();
    } else {
      this.calendar.next();
    }
    const startTime = new Date(this.calendar.getCurrentData().viewApi.activeStart).getTime() / 1000;
    const endTime = new Date(this.calendar.getCurrentData().viewApi.activeEnd).getTime() / 1000;
    this.previosNextClicked.emit({ direction, view: currentView, currentViewRange: { startTime, endTime } });
  }

  handleUpdateGroupEvent(e: Event, option: { label: string; value: 'this' | 'future' | 'all' }) {
    e.preventDefault();
    e.stopPropagation();
    if (this.isLoading) return;
    this.loadingOptions.update = option.value;
    if (this.selectedEvent && this.internals.checkValidity()) {
      this.saveGroupEventChanges.emit({ data: this.selectedEvent, action: option.value });
    }
  }

  handleDeleteGroupEvent(e: Event, option: { label: string; value: string }) {
    e.preventDefault();
    e.stopPropagation();
    if (this.isLoading) return;
    this.loadingOptions.delete = option.value;
    if (this.selectedEvent) {
      this.deleteGroupEvent.emit({ data: this.selectedEvent, action: option.value as 'this' | 'future' | 'all' });
    }
  }

  async handleCreateGroupEvent(e: Event) {
    e.preventDefault();
    e.stopPropagation();
    const formData = new FormData(this.formRef);
    const valid = this.formRef.checkValidity();
    if (!valid) {
      this.formError = i18next.t('fixFormErrors');
      this.formErrors['form'] = i18next.t('fixFormErrors');
      return;
    }
    const formState = {} as {
      'title': string;
      'description': string;
      'location': string;
      'event-timeslot': string;
      'event-capacity': string;
      'conferencing': string;
      'participants': string;
    };
    formData.forEach((value, key) => {
      formState[key] = value;
    });
    await this.createGroupEvent.emit({
      title: formState?.title,
      description: formState?.description,
      location: formState?.location,
      eventTimeslot: JSON.parse(formState?.['event-timeslot']),
      eventCapacity: parseInt(formState?.['event-capacity']),
      conferencing: formState?.conferencing ? JSON.parse(formState?.conferencing.toString()) : undefined,
      participants: formState?.participants ? JSON.parse(formState.participants) : [],
    });
  }

  @Watch('isLoading')
  isLoadingChangedHandler(newVal: boolean, _oldVal: boolean) {
    if (!newVal) {
      this.loadingOptions = {
        update: '',
        delete: '',
        create: '',
      };
      this.showCreateEventDetails = false;
      this.createNewGroupEventTimeslot = null;
      this.showEditOptions = false;
      this.showUpdateOptions = false;
      this.showDeleteOptions = false;
      this.showEditEventDetails = false;
      this.unsavedChanges = false;
      this.showConfirmDiscardUnsavedChangesModal = false;
      this.selectedEvent = null;
      this.selectedImportParticipants = [];
      this.importParticipants = [];
      this.showChooseParticipantsModal = false;
      this.formError = '';
      this.formErrors = {};
    }
  }

  @Listen('valueChanged')
  valueChangedHandler(event: CustomEvent<{ value: string; name: string }>) {
    const { value, name } = event.detail;
    const componentNames = ['title', 'description', 'location', 'event-timeslot', 'participants', 'event-capacity'];
    if ((this.showEditEventDetails || this.showCreateEventDetails) && componentNames.includes(name)) {
      this.unsavedChanges = true;

      // Clear error for this specific field only, not all errors
      if (this.formErrors[name]) {
        delete this.formErrors[name];
      }

      // Only clear formError if all fields are valid
      if (Object.keys(this.formErrors).length === 0) {
        this.formError = '';
        this.internals.setValidity({});
      } else {
        // Keep the first error message displayed
        const firstErrorMessage = Object.values(this.formErrors)[0];
        this.formError = firstErrorMessage || '';
      }
    }

    if (name === 'event-timeslot') {
      const { recurrence } = JSON.parse(value);
      const currentRrule = this.selectedEvent?.group_event_info?.rrule?.[0];
      const updatedRrule = recurrence[0];
      if (currentRrule !== updatedRrule) {
        this.updateOptionTranslations();
        this.updateOptions = this.updateOptionChoices.filter(option => option.value !== 'this');
        return;
      }
      if (this.selectedEvent?.group_event_info) {
        this.selectedEvent.group_event_info.rrule = recurrence;
      }
    }
    if (name === 'event-capacity') {
      const parsedValue = parseInt(value);
      if (parsedValue < 1 || parsedValue > 500 || isNaN(parsedValue)) {
        this.formErrors['event-capacity'] = i18next.t('fixFormErrors');
        this.formError = i18next.t('fixFormErrors');
        this.internals.setValidity({ customError: true }, this.formError);
      } else {
        this.formError = '';
        this.internals.setValidity({ customError: false }, '');
      }
    }
  }

  @Listen('timeWindowFormError')
  timeWindowFormErrorHandler(event: CustomEvent<{ key: string; message: string; uniqueIdentifier?: string }>) {
    if (event.detail.key === 'event-timeslot') {
      const fieldIdentifier = event.detail?.uniqueIdentifier || event.detail.key;
      if (event.detail.message) {
        // Store the error with the field's name as the key
        this.formErrors[fieldIdentifier] = event.detail.message;
        this.formError = event.detail.message;
        this.internals.setValidity({ customError: true }, event.detail.message);
      } else {
        // If the error is cleared for this field
        if (this.formErrors[fieldIdentifier]) {
          delete this.formErrors[fieldIdentifier];
        }

        // Only clear formError if all fields are valid
        if (Object.keys(this.formErrors).length === 0) {
          this.formError = '';
          this.internals.setValidity({});
        } else {
          // Keep the first error message displayed
          const firstErrorMessage = Object.values(this.formErrors)[0];
          this.formError = firstErrorMessage || '';
        }
      }
    }
  }

  @Listen('dateTimeInputError')
  dateTimeInputErrorHandler(event: CustomEvent<{ key: string; message: string }>) {
    if (event.detail.key === 'event-timeslot') {
      if (event.detail.message) {
        this.formErrors['date-time-input'] = event.detail.message;
        this.formError = event.detail.message;
      } else {
        delete this.formErrors['date-time-input'];
        this.formError = '';
      }
    }
  }

  @Listen('saveGroupEventChangesError', { target: 'document' })
  saveGroupEventChangesErrorHandler(event: CustomEvent<{ error: string }>) {
    this.loadingOptions.update = '';
    if (event.detail.error) {
      this.formError = event.detail.error;
    }
  }

  @Listen('selectedOptionsChanged')
  selectedOptionsChangedHandler(event: CustomEvent<{ value: string[]; name: string }>) {
    const { value, name } = event.detail;
    if (name === 'participants') {
      this.selectedImportParticipants = this.selectedEvent?.event.participants?.filter(participant => value.includes(participant.email)) || [];
    }
  }

  @Listen('nylasCheckboxGroupChanged')
  nylasCheckboxGroupChangedHandler(event: CustomEvent<{ selectedValues: string[]; name: string }>) {
    const { selectedValues, name } = event.detail;
    if (name === 'hide-non-group-events') {
      this.showAllEvents = !selectedValues.includes('group-only');
      let events = JSON.parse(JSON.stringify(this.events));
      if (!this.showAllEvents) {
        events = events.filter(event => event.classNames.includes('group-event'));
      }
      this.calendar?.setOption('events', events);
    }
  }

  @RegisterComponent<NylasEventCalendar, NylasSchedulerConfigConnector, Exclude<NylasSchedulerEditor['stores'], undefined>>({
    name: 'nylas-event-calendar',
    stateToProps: new Map([
      ['schedulerConfig.selectedLanguage', 'selectedLanguage'],
      ['schedulerConfig.groupEvents', 'groupEvents'],
      ['schedulerConfig.masterGroupEvents', 'masterGroupEvents'],
      ['schedulerConfig.currentUser', 'currentUser'],
      ['schedulerConfig.calendars', 'calendars'],
      ['schedulerConfig.isLoading', 'isLoading'],
      ['schedulerConfig.selectedConfiguration', 'selectedConfiguration'],
    ]),
    eventToProps: {
      groupEventSelected: async (event: CustomEvent<GroupEventAPIData>, connector: NylasSchedulerConfigConnector) => {
        const { detail } = event;
        connector.schedulerConfigStore.state.selectedGroupEvent = detail;
      },
      calendarViewChanged: async (event: CustomEvent<{ startTime: number; endTime: number }>, connector: NylasSchedulerConfigConnector) => {
        const { detail } = event;
        connector.schedulerConfigStore.state.calendarViewDateRange = {
          start_time: detail.startTime.toString(),
          end_time: detail.endTime.toString(),
        };
        const configId = connector.schedulerConfigStore.state.selectedConfiguration?.id;
        if (configId) {
          const [data, error] = await connector.schedulerConfig.getGroupEvents(configId, connector.schedulerConfigStore.state.selectedConfiguration as GroupConfiguration);
          if (!data && error && error.message) {
            // handle error
            return;
          }
        }
      },
      previosNextClicked: async (
        event: CustomEvent<{ direction: 'prev' | 'next'; view: 'day' | 'week' | 'month'; currentViewRange: { startTime: number; endTime: number } }>,
        connector: NylasSchedulerConfigConnector,
      ) => {
        const { detail } = event;
        connector.schedulerConfigStore.state.calendarViewDateRange = {
          start_time: detail.currentViewRange.startTime.toString(),
          end_time: detail.currentViewRange.endTime.toString(),
        };
        const configId = connector.schedulerConfigStore.state.selectedConfiguration?.id;
        if (configId) {
          const [data, error] = await connector.schedulerConfig.getGroupEvents(configId, connector.schedulerConfigStore.state.selectedConfiguration as GroupConfiguration);
          if (!data && error && error.message) {
            // handle error
            return;
          }
        }
      },
      saveGroupEventChanges: async (event: CustomEvent<{ data: GroupEventAPIData; action: 'this' | 'future' | 'all' }>, _connector: NylasSchedulerConfigConnector) => {
        debug('nylas-event-calendar', 'saveGroupEventChanges', event.detail);
      },
      setCalendarViewDateRange: async (event: CustomEvent<{ startTime: number; endTime: number }>, connector: NylasSchedulerConfigConnector) => {
        debug('nylas-event-calendar', 'setCalendarViewDateRange', event.detail);
        connector.schedulerConfigStore.state.calendarViewDateRange = {
          start_time: event.detail.startTime.toString(),
          end_time: event.detail.endTime.toString(),
        };
      },
      fetchMasterGroupEvent: async (event: CustomEvent<string>, connector: NylasSchedulerConfigConnector) => {
        debug('nylas-event-calendar', 'fetchMasterGroupEvent', event.detail);
        const [data, error] = await connector.schedulerConfig.getMasterEventById(event.detail);
        if (!data && error && error.message) {
          // handle error
          return;
        }
      },
    },
    fireRegisterEvent: true,
  })
  render() {
    const calendarId = this.selectedConfiguration?.group_booking?.calendar_id;
    const currentCalendar = calendarId ? this.calendars?.find(c => c.id === calendarId) : null;
    const day = i18next.t('timePeriod_day') || 'day';
    const week = i18next.t('timePeriod_week') || 'week';
    const month = i18next.t('timePeriod_month') || 'month';
    const start = this.selectedEvent?.event?.when?.start_time;
    const timezone = this.selectedEvent?.event?.when?.start_timezone;
    const startDate = start ? dayjs(start * 1000).tz(timezone) : null;
    const eventInPast = startDate && startDate.isBefore(dayjs().tz(timezone));

    return (
      <Host>
        <div class="nylas-event-calendar">
          <div
            class={{
              'calendar-container': true,
              'show-event': this.showEditEventDetails || this.showCreateEventDetails,
            }}
          >
            <div class="calendar-header">
              <div class="calendar-header__row_one">
                <div class="calendar-header__title">
                  <div class="calendar-header-prev-next">
                    <button onClick={() => this.handlePrevNextClick('prev')} class={{ 'chevron-left': true, 'button': true }}>
                      {' '}
                      <chevron-icon />
                    </button>
                    <span>{this.selectedDateRangeString}</span>
                    <button onClick={() => this.handlePrevNextClick('next')} class={{ 'chevron-right': true, 'button': true }}>
                      {' '}
                      <chevron-icon />
                    </button>
                  </div>
                </div>
                <div class="calendar-header__current-calendar">{currentCalendar?.name || currentCalendar?.id}'s calendar</div>
                <div class="calendar-header__view-options">
                  <button class={{ selected: this.calendar?.view.type === typeToViewMapping['day'] }} onClick={() => this.setView('day')}>
                    {day.charAt(0).toUpperCase() + day.slice(1)}
                  </button>
                  <button class={{ selected: this.calendar?.view.type === typeToViewMapping['week'] }} onClick={() => this.setView('week')}>
                    {week.charAt(0).toUpperCase() + week.slice(1)}
                  </button>
                  <button class={{ selected: this.calendar?.view.type === typeToViewMapping['month'] }} onClick={() => this.setView('month')}>
                    {month.charAt(0).toUpperCase() + month.slice(1)}
                  </button>
                </div>
              </div>
              <div class="calendar-header__row_two">
                <div class="calendar-header__hide-non-group-events">
                  <label>
                    {i18next.t('nylasEventCalendar.hideNonGroupEvents.showLabel')}
                    <span class="label-icon">
                      <tooltip-component>
                        <info-icon slot="tooltip-icon" />
                        <span slot="tooltip-content">{i18next.t('nylasEventCalendar.hideNonGroupEvents.tooltip.desc')}</span>
                      </tooltip-component>
                    </span>
                  </label>
                  <checkbox-group
                    direction="horizontal"
                    name="hide-non-group-events"
                    options={[{ label: i18next.t('nylasEventCalendar.hideNonGroupEvents.options.group'), value: 'group-only', checked: false }]}
                  />
                </div>
              </div>
            </div>
            <div ref={el => (this.calendarEl = el as HTMLDivElement)}></div>
          </div>
          {this.showEditEventDetails && this.selectedEvent?.group_event_info && (
            <div class="event-details-container">
              <div class="event-details-header">
                <h3>{i18next.t('nylasEventCalendar.editEventDetails.headerTitle')}</h3>
                <div class="event-details-header__actions">
                  <button-component
                    tooltip={i18next.t('nylasEventCalendar.editEventDetails.closeButtonTooltip')}
                    id="close"
                    disabled={this.isLoading}
                    variant={'invisible'}
                    onClick={e => {
                      e.preventDefault();
                      e.stopImmediatePropagation();
                      if (this.unsavedChanges) {
                        this.showConfirmDiscardUnsavedChangesModal = true;
                      } else {
                        this.showEditEventDetails = false;
                        this.unsavedChanges = false;
                        this.selectedEvent = null;
                        this.formError = '';
                      }
                    }}
                  >
                    <close-icon />
                  </button-component>
                </div>
              </div>
              <div
                class={{
                  'event-details': true,
                  'edit-event-details': true,
                }}
              >
                <nylas-event-title
                  noTemplates={true}
                  name="title"
                  eventTitle={this.selectedEvent.event.title}
                  exportparts="net, net__title, net__input_group, net__input_wrapper, net__input"
                />
                <nylas-event-description
                  name="description"
                  noTemplates={true}
                  eventDescription={this.selectedEvent.event.description}
                  exportparts="ned, ned__title, ned__input_group, ned__input_wrapper, ned__input"
                />
                <nylas-event-location
                  name="location"
                  eventLocation={this.selectedEvent.event.location}
                  eventConferencing={this.selectedEvent.event.conferencing}
                  exportparts="nel, nel__title, nel__input_group, nel__input_wrapper, nel__input, nel__conferencing"
                />
                <nylas-event-timeslot
                  name="event-timeslot"
                  initialValue={{
                    when: this.selectedEvent.event.when,
                    recurrence: this.selectedEvent?.group_event_info?.rrule ? this.selectedEvent.group_event_info.rrule : [],
                  }}
                  exportparts="nets, nets__title, nets__input_group, nets__input_wrapper, nets__input, nets__timezone, nets__recurrence"
                />
                <nylas-event-capacity
                  name="event-capacity"
                  capacity={this.selectedEvent?.group_event_info?.capacity}
                  exportparts="nec, nec__title, nec__input_group, nec__input_wrapper, nec__input"
                />
                <nylas-additional-participants
                  name="participants"
                  eventParticipants={this.selectedEvent.group_event_info.participants}
                  exportparts="nadtnlpart, nadtnlpart__title, nadtnlpart__subtitle, nadtnlpart__content, nadtnlpart__input_group, nadtnlpart__input_wrapper, nadtnlpart__input, nadtnlpart__remove-participant"
                />
              </div>
              <div class="event-details-footer">
                <div class="event-details-footer__error">
                  <p>{this.formError}</p>
                </div>
                <div class="event-details-footer__helper-text">
                  <p>{this.unsavedChanges && !this.isLoading ? i18next.t('nylasEventCalendar.unsavedChanges') : ''}</p>
                </div>
                <div class="event-details-footer__actions">
                  <button-component
                    variant={'destructive'}
                    disabled={this.isLoading}
                    onClick={e => {
                      e.preventDefault();
                      e.stopImmediatePropagation();
                      if (this.selectedEvent?.event?.master_event_id) {
                        this.showUpdateOptions = true;
                      } else {
                        this.handleDeleteGroupEvent(e, { label: 'Delete this event', value: 'this' });
                      }
                    }}
                  >
                    {i18next.t('nylasEventCalendar.editEventDetails.deleteButton')}
                  </button-component>
                  <button-component
                    variant={'basic'}
                    disabled={this.isLoading || this.formError !== '' || !!eventInPast}
                    tooltip={eventInPast ? i18next.t('nylasEventCalendar.editEventDetails.pastEventTooltip') : ''}
                    onClick={e => {
                      e.preventDefault();
                      e.stopImmediatePropagation();
                      if (!this.unsavedChanges) return;
                      if (this.selectedEvent?.event?.master_event_id) {
                        this.showUpdateOptions = true;
                      } else {
                        this.handleUpdateGroupEvent(e, { label: 'Update this event', value: 'this' });
                      }
                    }}
                  >
                    {i18next.t('nylasEventCalendar.editEventDetails.saveButton')}
                  </button-component>
                </div>
              </div>
            </div>
          )}
          {this.showCreateEventDetails && this.createNewGroupEventTimeslot && (
            <div class="event-details-container">
              <div class="event-details-header">
                <h3>{i18next.t('nylasEventCalendar.createEventDetails.headerTitle')}</h3>
                <div class="event-details-header__actions">
                  <button-component
                    tooltip={i18next.t('nylasEventCalendar.createEventDetails.closeButtonTooltip')}
                    id="close"
                    variant={'invisible'}
                    disabled={this.isLoading}
                    onClick={e => {
                      e.preventDefault();
                      e.stopImmediatePropagation();
                      if (this.unsavedChanges) {
                        this.showConfirmDiscardUnsavedChangesModal = true;
                      } else {
                        this.showCreateEventDetails = false;
                        this.createNewGroupEventTimeslot = null;
                        this.selectedEvent = null;
                        this.unsavedChanges = false;
                      }
                    }}
                  >
                    <close-icon />
                  </button-component>
                </div>
              </div>
              <form ref={el => (this.formRef = el as HTMLFormElement)}>
                <div
                  class={{
                    'event-details': true,
                  }}
                >
                  <nylas-event-info>
                    <div slot="inputs" class="event-details-form-inputs">
                      <nylas-event-title noTemplates={true} eventTitle={'New group event'} exportparts="net, net__title, net__input_group, net__input_wrapper, net__input" />
                      <nylas-event-description noTemplates={true} hideAutoPopulateButton={true} exportparts="ned, ned__title, ned__input_group, ned__input_wrapper, ned__input" />
                      <nylas-event-location
                        userProvider={this.currentUser?.provider}
                        exportparts="nel, nel__title, nel__input_group, nel__input_wrapper, nel__input, nel__conferencing"
                      />
                      <nylas-event-timeslot
                        name="event-timeslot"
                        initialValue={this.createNewGroupEventTimeslot}
                        exportparts="nets, nets__title, nets__input_group, nets__input_wrapper, nets__input, nets__timezone, nets__recurrence"
                      />
                      <nylas-event-capacity name="event-capacity" exportparts="nec, nec__title, nec__input_group, nec__input_wrapper, nec__input" />
                    </div>
                  </nylas-event-info>
                  <nylas-additional-participants
                    name="participants"
                    eventParticipants={[{ name: this.currentUser?.name || this.currentUser?.email, email: this.currentUser?.email || '', is_organizer: true }]}
                    exportparts="nadtnlpart, nadtnlpart__title, nadtnlpart__subtitle, nadtnlpart__content, nadtnlpart__input_group, nadtnlpart__input_wrapper, nadtnlpart__input, nadtnlpart__remove-participant"
                  />
                </div>
                <div class="event-details-footer">
                  <div class="event-details-footer__error">
                    <p>{this.formError}</p>
                  </div>
                  <div class="event-details-footer__helper-text">
                    <p>{this.unsavedChanges && !this.isLoading ? i18next.t('nylasEventCalendar.unsavedChanges') : ''}</p>
                  </div>
                  <div class="event-details-footer__actions">
                    <button-component
                      variant={'basic'}
                      disabled={this.isLoading}
                      onClick={e => {
                        e.preventDefault();
                        e.stopImmediatePropagation();
                        if (this.unsavedChanges) {
                          this.showConfirmDiscardUnsavedChangesModal = true;
                        } else {
                          this.showCreateEventDetails = false;
                          this.createNewGroupEventTimeslot = null;
                          this.selectedEvent = null;
                          this.unsavedChanges = false;
                        }
                      }}
                    >
                      {i18next.t('nylasEventCalendar.createEventDetails.cancelButton')}
                    </button-component>
                    <button-component
                      variant={'primary'}
                      disabled={this.isLoading || this.formError !== ''}
                      isLoading={this.isLoading}
                      onClick={e => {
                        this.handleCreateGroupEvent(e);
                      }}
                    >
                      {i18next.t('nylasEventCalendar.createEventDetails.createButton')}
                    </button-component>
                  </div>
                </div>
              </form>
            </div>
          )}
          {this.showConfirmDiscardUnsavedChangesModal && (
            <div class="discard-changes action-options" onClick={() => (this.showUpdateOptions = false)}>
              <div class="discard-changes__card action-options__card">
                <button-component
                  tooltip="Close"
                  id="close-discard-changes"
                  variant={'invisible'}
                  disabled={this.isLoading}
                  onClick={e => {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                    this.showConfirmDiscardUnsavedChangesModal = false;
                  }}
                >
                  <close-icon />
                </button-component>
                <div class="discard-changes__content action-options__content">
                  <h3 slot="card-title" part="nfbf__title">
                    {i18next.t('nylasEventCalendar.discardChanges.headerTitle')}
                  </h3>
                </div>
                <div class="discard-changes__footer action-options__footer">
                  <button-component variant={'basic'} onClick={_e => (this.showConfirmDiscardUnsavedChangesModal = false)} part="nfbf__button-outline nfbf__cancel-cta">
                    {i18next.t('nylasEventCalendar.discardChanges.cancelButton')}
                  </button-component>
                  <button-component
                    variant={'destructive'}
                    onClick={e => {
                      e.preventDefault();
                      e.stopImmediatePropagation();
                      this.showConfirmDiscardUnsavedChangesModal = false;
                      this.unsavedChanges = false;
                      this.showEditEventDetails = false;
                      this.showCreateEventDetails = false;
                      this.selectedEvent = null;
                      this.createNewGroupEventTimeslot = null;
                      this.formError = '';
                    }}
                    part="nfbf__button-outline nfbf__reschedule-cta"
                  >
                    {i18next.t('nylasEventCalendar.discardChanges.discardButton')}
                  </button-component>
                </div>
              </div>
            </div>
          )}
          {this.showEditOptions && (
            <div class="edit-options action-options" onClick={() => (this.showEditOptions = false)}>
              <div class="edit-options__card action-options__card">
                <div class="edit-options__header action-options__header">
                  <h3>{i18next.t('nylasEventCalendar.editOptions.headerTitle')}</h3>
                  <button-component
                    tooltip={i18next.t('nylasEventCalendar.editOptions.closeButtonTooltip')}
                    id="close-edit-options"
                    disabled={this.isLoading}
                    variant={'invisible'}
                    onClick={e => {
                      e.preventDefault();
                      e.stopImmediatePropagation();
                      this.showEditOptions = false;
                    }}
                  >
                    <close-icon />
                  </button-component>
                </div>
                <div class="edit-options__content action-options__content">
                  <ul tabindex="-1" role="listbox" aria-label={this.name}>
                    {this.editOptions.map(option =>
                      option.value.toString() ? (
                        <li
                          class={{ disabled: !!this.isLoading }}
                          tabindex="0"
                          key={option.value}
                          id={option.value}
                          onClick={e => this.handleEditOptionSelected(e, option)}
                          role="option"
                        >
                          {this.isLoading && this.loadingOptions.delete === option.value ? i18next.t('nylasEventCalendar.editOptions.deleting') : option.label}
                          {option.value === 'import' && (
                            <tooltip-component id="organizer_participant_tooltip">
                              <info-icon slot="tooltip-icon" />
                              <span slot="tooltip-content">{i18next.t('nylasEventCalendar.editOptions.importTooltip')}</span>
                            </tooltip-component>
                          )}
                        </li>
                      ) : null,
                    )}
                  </ul>
                </div>
              </div>
            </div>
          )}
          {this.showUpdateOptions && (
            <div class="update-options action-options" onClick={() => (this.showUpdateOptions = false)}>
              <div class="update-options__card action-options__card">
                <button-component
                  tooltip="Close"
                  id="close-update-options"
                  variant={'invisible'}
                  disabled={this.isLoading}
                  onClick={e => {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                    this.showUpdateOptions = false;
                  }}
                >
                  <close-icon />
                </button-component>
                <div class="update-options__content action-options__content">
                  <ul tabindex="-1" role="listbox" aria-label={this.name}>
                    {this.updateOptions.map(option =>
                      option.value.toString() ? (
                        <li
                          class={{ disabled: !!this.isLoading }}
                          tabindex="0"
                          key={option.value}
                          id={option.value}
                          role="option"
                          onClick={e => this.handleUpdateGroupEvent(e, option)}
                        >
                          {this.isLoading && this.loadingOptions.update === option.value ? `Updating...` : option.label}
                        </li>
                      ) : null,
                    )}
                  </ul>
                </div>
              </div>
            </div>
          )}
          {this.showDeleteOptions && (
            <div class="delete-options action-options" onClick={() => (this.showDeleteOptions = false)}>
              <div class="delete-options__card action-options__card">
                <button-component
                  tooltip={i18next.t('nylasEventCalendar.deleteEvent.closeButtonTooltip')}
                  id="close-delete-options"
                  variant={'invisible'}
                  disabled={this.isLoading}
                  onClick={e => {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                    this.showDeleteOptions = false;
                  }}
                >
                  <close-icon />
                </button-component>
                <div class="delete-options__content action-options__content">
                  <ul tabindex="-1" role="listbox" aria-label={this.name}>
                    {this.deleteOptions.map(option =>
                      option.value.toString() ? (
                        <li
                          class={{ disabled: !!this.isLoading }}
                          tabindex="0"
                          key={option.value}
                          id={option.value}
                          role="option"
                          onClick={e => this.handleDeleteGroupEvent(e, option)}
                        >
                          {this.isLoading && this.loadingOptions.delete === option.value ? i18next.t('nylasEventCalendar.deleteEvent.deleting') : option.label}
                        </li>
                      ) : null,
                    )}
                  </ul>
                </div>
              </div>
            </div>
          )}
          {this.showChooseParticipantsModal && (
            <div class="choose-participants-modal action-options">
              <div class="choose-participants-modal__card action-options__card">
                <button-component
                  tooltip={i18next.t('nylasEventCalendar.importEvent.closeButtonTooltip')}
                  id="close-choose-participants-modal"
                  variant={'invisible'}
                  disabled={this.isLoading}
                  onClick={e => {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                    this.showChooseParticipantsModal = false;
                  }}
                >
                  <close-icon />
                </button-component>
                <div class="choose-participants-modal__content action-options__content">
                  <multi-select-dropdown
                    name="participants"
                    multipleOptionsSelectedLabel={i18next.t('multipleOptionsSelected')}
                    selectAtLeastOneOptionLabel={i18next.t('selectOneOrMoreOptions')}
                    label={i18next.t('nylasEventCalendar.importEvent.participantsLabel')}
                    options={this.importParticipants}
                  />
                  <button-component variant={'basic'} onClick={() => this.handleImportGroupEvent()}>
                    {i18next.t('nylasEventCalendar.importEvent.importButton')}
                  </button-component>
                </div>
              </div>
            </div>
          )}
        </div>
      </Host>
    );
  }
}
